/**
 * section-sw-pdp-gallery.js — PDP gallery layer swaps + scroll-driven crossfade.
 *
 * Two independent mechanics:
 *   1. Click/event-driven swaps (thumbs, finish tiles via `sw:gallery-show`)
 *      — plain JS + CSS transitions. Works with zero GSAP, and instantly under
 *      prefers-reduced-motion (transition removed in CSS).
 *   2. Scroll-driven crossfade (h3 §5) — registered THROUGH the SWAnimations
 *      gate (assets/animations.js), desktop >=1024px only, ScrollTrigger
 *      onEnter/onEnterBack, no pins ever (c6 X-04).
 *
 * SELECTOR CONTRACT (prototype defect D-5 guard — docs/wave/h-h-prototype-gate.md):
 * panels carrying [data-image-target] live in the SCROLLING CONFIG COLUMN
 * (product-info / addon-upsell / product-kit-builder — sibling sections),
 * NOT inside this gallery section. The panel query below is therefore made
 * at document level. Never scope it to the gallery root: that is exactly the
 * bug that silenced the prototype's crossfade (zero triggers created).
 */
(function () {
  'use strict';
  if (window.SWPdpGallery) return;

  function roots() {
    return document.querySelectorAll('[data-sw-pdp-gallery]');
  }

  function syncThumbs(root, id) {
    root.querySelectorAll('[data-sw-thumb]').forEach(function (thumb) {
      thumb.classList.toggle('is-selected', thumb.getAttribute('data-target') === id);
    });
  }

  function swapTo(root, id) {
    if (!root || !id) return;
    var next = root.querySelector('.pdp-image-layer[data-layer="' + id + '"]');
    if (!next) return; // unknown target (panel points at a media slot this product lacks) — keep current image
    var current = root.querySelector('.pdp-image-layer.is-current');
    if (next === current) return;
    if (current) current.classList.remove('is-current');
    next.classList.add('is-current');
    syncThumbs(root, id);
  }

  function init(root) {
    if (root.hasAttribute('data-sw-gallery-ready')) return;
    root.setAttribute('data-sw-gallery-ready', '');
    root.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-sw-thumb]');
      if (!thumb) return;
      swapTo(root, thumb.getAttribute('data-target'));
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-sw-pdp-gallery]').forEach(init);
  }

  window.SWPdpGallery = { swapTo: swapTo };

  /* Cross-section swap requests (finish tiles in product-info, etc.) */
  document.addEventListener('sw:gallery-show', function (event) {
    var detail = event.detail || {};
    if (!detail.layer) return;
    roots().forEach(function (root) {
      swapTo(root, detail.layer);
    });
  });

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  }
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  /* Scroll-driven crossfade — only through the SWAnimations gate. The gate
     guarantees: no timelines under prefers-reduced-motion, IntersectionObserver
     deferral, and theme-editor lifecycle cleanup. `immediate: true` because the
     gallery is above the fold on every PDP (the LCP image itself is rendered
     visible from the server and is never touched by this code). */
  if (window.SWAnimations) {
    window.SWAnimations.register(
      'sw-pdp-gallery-crossfade',
      '[data-sw-animate="pdp-gallery"]',
      function (el, gsap) {
        if (!window.ScrollTrigger || typeof gsap.matchMedia !== 'function') return;
        var mm = gsap.matchMedia();
        mm.add('(min-width: 1024px)', function () {
          var triggers = [];
          /* Document-level panel query — see SELECTOR CONTRACT above.
             Panels: scrolling config column. Layers: this gallery. */
          document.querySelectorAll('[data-image-target]').forEach(function (panel) {
            var target = panel.getAttribute('data-image-target');
            if (!target) return;
            triggers.push(
              window.ScrollTrigger.create({
                trigger: panel,
                start: 'top center',
                onEnter: function () {
                  swapTo(el, target);
                },
                onEnterBack: function () {
                  swapTo(el, target);
                }
              })
            );
          });
          return function () {
            triggers.forEach(function (trigger) {
              trigger.kill();
            });
          };
        });
        return function () {
          mm.revert();
        };
      },
      { immediate: true }
    );
  }
})();
